import { NavLink, useLocation } from "react-router-dom";
import "../../App.css";
import { useState, useRef } from "react";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import {
  Users,
  CreditCard,
  BarChart2,
  ChevronRight,
  User,
  Wallet,
  PieChart,
  HandCoins,
} from "lucide-react";
import { FourCircles } from "./Circles";

const Leftbar = () => {
  const location = useLocation();

  const [openMenus, setOpenMenus] = useState({});
  const menuRefs = useRef({});

  const toggleMenu = (id) => {
    setOpenMenus((prev) => {
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false; // close all menus first
        return acc;
      }, {});

      // If the clicked menu had a submenu, toggle it open
      const clickedMenu = menuItems.find((m) => m.id === id);
      if (clickedMenu?.submenus) {
        newState[id] = !prev[id];
      }

      return newState;
    });
  };

  const isActive = (route) => location.pathname === route;

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      route: "/dashboard",
      icon: <FourCircles size={20} color="#FFFFFF8F" />,
      permissions: [
        PERMISSIONS?.DB?.VIEW_CASH,
        PERMISSIONS?.DB?.VIEW_LOAN_VOLUME,
        PERMISSIONS?.DB?.VIEW_OUTSTANDING,
        PERMISSIONS?.DB?.VIEW_DEFAULTED,
        PERMISSIONS?.DB?.VIEW_ACTIVE_ACCOUNTS,
        PERMISSIONS?.DB?.VIEW_ACTIVITY,
        PERMISSIONS?.DB?.VIEW_DEPOSITS,
        PERMISSIONS?.DB?.VIEW_SHARES_VOLUME,
        PERMISSIONS?.DB?.VIEW_MANUAL_REVIEW,
      ],
    },
    {
      id: "members",
      label: "Members",
      route: "/dashboard/customers",
      icon: <Users size={20} color="#FFFFFF8F" />,
      permissions: [
        PERMISSIONS?.MB?.VIEW_ALL,
        PERMISSIONS?.MB?.ADD_NEW,
        PERMISSIONS?.MB?.EXPORT_LIST,
        PERMISSIONS?.MB?.VIEW_DETAILS,
        PERMISSIONS?.MB?.EDIT_INFO,
      ],
    },
    {
      id: "transactions",
      label: "Transactions",
      route: null,
      icon: <CreditCard size={20} color="#FFFFFF8F" />,
      permissions: [
        PERMISSIONS?.TX?.VIEW_ALL,
        PERMISSIONS?.TX?.VIEW_SUMMARY,
        PERMISSIONS?.TX?.UPLOAD_BULK,
      ],
      submenus: [
        {
          id: "all-transactions",
          label: "All Transactions",
          route: "/dashboard/transactions",
          permissions: [
            PERMISSIONS?.TX?.VIEW_ALL,
            PERMISSIONS?.TX?.VIEW_SUMMARY,
          ],
        },
        {
          id: "bulk-repayment",
          label: "Bulk Payments",
          route: "/dashboard/bulk-repayment",
          permissions: [PERMISSIONS?.TX?.UPLOAD_BULK],
        },
      ],
    },
    {
      id: "loans",
      label: "Loans",
      route: null,
      icon: <HandCoins size={20} color="#FFFFFF8F" />,
      permissions: [
        PERMISSIONS?.LN?.VIEW_BOOK,
        PERMISSIONS?.LN?.VIEW_APPLICATIONS,
        PERMISSIONS?.LN?.APPROVE_REJECT,
        PERMISSIONS?.LN?.VIEW_VOLUME,
        PERMISSIONS?.LN?.MODIFY_TERMS,
        PERMISSIONS?.LN?.VIEW_FILTERED,
        PERMISSIONS?.LN?.EXPORT,
        PERMISSIONS?.LN?.DISBURSE,
        PERMISSIONS?.LN?.UPDATE_AMOUNT,
        PERMISSIONS?.LN?.UPDATE_TERMS,
        PERMISSIONS?.LN?.VIEW_DETAILS,
        PERMISSIONS?.LN?.VIEW_GUARANTORS_SUMMARY,
        PERMISSIONS?.LN?.VIEW_ALL_GUARANTORS,
        PERMISSIONS?.LN?.EXPORT_GUARANTORS,
      ],
      submenus: [
        {
          id: "loans-list",
          label: "Loans Management",
          route: "/dashboard/loans",
          permissions: [
            PERMISSIONS?.LN?.VIEW_BOOK,
            PERMISSIONS?.LN?.VIEW_APPLICATIONS,
            PERMISSIONS?.LN?.APPROVE_REJECT,
            PERMISSIONS?.LN?.VIEW_VOLUME,
            PERMISSIONS?.LN?.MODIFY_TERMS,
            PERMISSIONS?.LN?.VIEW_FILTERED,
            PERMISSIONS?.LN?.EXPORT,
            PERMISSIONS?.LN?.DISBURSE,
            PERMISSIONS?.LN?.UPDATE_AMOUNT,
            PERMISSIONS?.LN?.UPDATE_TERMS,
            PERMISSIONS?.LN?.VIEW_DETAILS,
          ],
        },
        {
          id: "loans-list",
          label: "Loan Applications",
          route: "/dashboard/loan_applications",
          permissions: [
            PERMISSIONS?.LN?.VIEW_BOOK,
            PERMISSIONS?.LN?.VIEW_APPLICATIONS,
            PERMISSIONS?.LN?.APPROVE_REJECT,
            PERMISSIONS?.LN?.VIEW_VOLUME,
            PERMISSIONS?.LN?.MODIFY_TERMS,
            PERMISSIONS?.LN?.VIEW_FILTERED,
            PERMISSIONS?.LN?.EXPORT,
            PERMISSIONS?.LN?.DISBURSE,
            PERMISSIONS?.LN?.UPDATE_AMOUNT,
            PERMISSIONS?.LN?.UPDATE_TERMS,
            PERMISSIONS?.LN?.VIEW_DETAILS,
          ],
        },
        {
          id: "guarantors",
          label: "Guarantors",
          route: "/dashboard/guarantors",
          permissions: [
            PERMISSIONS?.LN?.VIEW_GUARANTORS_SUMMARY,
            PERMISSIONS?.LN?.VIEW_ALL_GUARANTORS,
            PERMISSIONS?.LN?.EXPORT_GUARANTORS,
          ],
        },
      ],
    },
    {
      id: "savings",
      label: "Savings",
      route: "/dashboard/savings",
      icon: <Wallet size={20} color="#FFFFFF8F" />,
      permissions: [PERMISSIONS?.SV?.VIEW_ALL],
    },
    {
      id: "shares",
      label: "Shares",
      route: "/dashboard/shares",
      icon: <PieChart size={20} color="#FFFFFF8F" />,
      permissions: [PERMISSIONS?.SH?.VIEW_ALL],
    },
    {
      id: "reports-analytics",
      label: "Reports & Analytics",
      route: null,
      icon: <BarChart2 size={20} color="#FFFFFF8F" />,
      permissions: [PERMISSIONS?.RP?.VIEW, PERMISSIONS?.RP?.VIEW_ANALYTICS],
      submenus: [
        {
          id: "reports",
          label: "Reports",
          route: "/dashboard/reports",
          permissions: [PERMISSIONS?.RP?.VIEW],
        },
        {
          id: "analytics",
          label: "Analytics",
          route: "/dashboard/analytics",
          permissions: [PERMISSIONS?.RP?.VIEW_ANALYTICS],
        },
      ],
    },
    {
      id: "user-management",
      label: "User Management",
      route: null,
      icon: <User size={20} color="#FFFFFF8F" />,
      permissions: [
        PERMISSIONS?.UM?.VIEW_ALL,
        PERMISSIONS?.UM?.UPDATE_INFO,
        PERMISSIONS?.UM?.SIGN_OUT,
        PERMISSIONS?.UM?.SUSPEND,
        PERMISSIONS?.UM?.DELETE,
        PERMISSIONS?.UM?.RESET_PASSWORD,
        PERMISSIONS?.UM?.ADD_NEW,
        PERMISSIONS?.UM?.EXPORT_LIST,
        PERMISSIONS?.UM?.MANAGE_ROLES,
        PERMISSIONS?.UM?.VIEW_AUDIT_TRAIL,
      ],
      submenus: [
        {
          id: "users",
          label: "Users",
          route: "/dashboard/userManagement/users",
          permissions: [
            PERMISSIONS?.UM?.VIEW_ALL,
            PERMISSIONS?.UM?.UPDATE_INFO,
            PERMISSIONS?.UM?.SIGN_OUT,
            PERMISSIONS?.UM?.SUSPEND,
            PERMISSIONS?.UM?.DELETE,
            PERMISSIONS?.UM?.RESET_PASSWORD,
            PERMISSIONS?.UM?.ADD_NEW,
            PERMISSIONS?.UM?.EXPORT_LIST,
            PERMISSIONS?.UM?.MANAGE_ROLES,
          ],
        },
        {
          id: "audit-trail",
          label: "Audit Trail",
          route: "/dashboard/userManagement/audit-trail",
          permissions: [PERMISSIONS?.UM?.VIEW_AUDIT_TRAIL],
        },
      ],
    },
  ];

  return (
    <div className="h-full bg-[#041422] leftbar">
      <div className="w-full h-full">
        <div className="h-[80px] w-[85%] mx-auto flex items-center justify-start">
          <div className="justify-start">
            <img className="h-[35px]" src="/logo_anansi.png" alt="anansi" />
          </div>
        </div>
        <div
          style={{
            height: "calc(100% - 80px - 8%)",
          }}
          className="w-full"
        >
          <div className="border border-[#2C3A45] mb-[10px] w-[85%] mx-auto"></div>
          <div className="h-full w-[85%] mx-auto flex flex-col gap-[20px] text-[#FFFFFF8F] text-[14px]">
            {menuItems.map((menu) => {
              const menuActive = menu.route && isActive(menu.route);

              return (
                <PermissionGate key={menu.id} required={menu.permissions}>
                  <div
                    className="relative"
                    ref={(el) => (menuRefs.current[menu.id] = el)}
                  >
                    {/* Menu Button */}
                    {menu.submenus ? (
                      <button
                        className={`flex items-center w-full h-[38px] rounded truncate px-2 gap-2 text-left ${
                          menuActive ? "bg-[#FFFFFF08] text-white" : ""
                        }`}
                        onClick={() => toggleMenu(menu.id)}
                      >
                        {menu.icon && <span>{menu.icon}</span>}
                        <span className="flex-1">{menu.label}</span>
                        <span
                          className={`transition-transform duration-300 ${
                            openMenus[menu.id] ? "rotate-90" : "rotate-0"
                          }`}
                        >
                          <ChevronRight size={18} />
                        </span>
                      </button>
                    ) : (
                      <NavLink
                        end
                        to={menu.route}
                        className={({ isActive }) =>
                          `flex items-center w-full h-[38px] px-2 gap-2 rounded ${
                            isActive ? "bg-gray-700" : ""
                          }`
                        }
                        onClick={() => toggleMenu(menu.id)}
                      >
                        {menu.icon && <span>{menu.icon}</span>}
                        {menu.label}
                      </NavLink>
                    )}

                    {/* Submenus */}
                    {/* Submenus */}
                    {menu.submenus && openMenus[menu.id] && (
                      <div className="ml-[40px] mt-1 relative flex flex-col menu-connector">
                        {menu.submenus.map((submenu, idx) => (
                          <PermissionGate
                            key={submenu.id}
                            required={submenu.permissions}
                          >
                            <NavLink
                              to={submenu.route}
                              className={({ isActive }) =>
                                `relative block px-4 py-2 text-sm ${
                                  isActive ? "bg-[#FFFFFF08] text-white" : ""
                                }`
                              }
                            >
                              {submenu.label}
                            </NavLink>
                          </PermissionGate>
                        ))}
                      </div>
                    )}
                  </div>
                </PermissionGate>
              );
            })}
          </div>
        </div>
        <div className="text-white h-[8%]">
          <NavLink
            to="/dashboard/settings"
            className="flex h-[42px] items-center gap-[10px] text-[#FFFFFF]"
          >
            <span className="pl-[20px] flex gap-[10px] items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19.4298 12.98C19.4698 12.66 19.4998 12.34 19.4998 12C19.4998 11.66 19.4698 11.34 19.4298 11.02L21.5398 9.36999C21.7298 9.21999 21.7798 8.94999 21.6598 8.72999L19.6598 5.26999C19.6009 5.16681 19.5071 5.088 19.3953 5.04769C19.2836 5.00738 19.1611 5.00819 19.0498 5.04999L16.5598 6.04999C16.0398 5.64999 15.4798 5.31999 14.8698 5.06999L14.4898 2.41999C14.4733 2.30233 14.4144 2.19475 14.3242 2.11742C14.234 2.0401 14.1186 1.99834 13.9998 1.99999H9.99984C9.74984 1.99999 9.53984 2.17999 9.50984 2.41999L9.12984 5.06999C8.51984 5.31999 7.95984 5.65999 7.43984 6.04999L4.94984 5.04999C4.89186 5.03032 4.83107 5.02018 4.76984 5.01999C4.59984 5.01999 4.42984 5.10999 4.33984 5.26999L2.33984 8.72999C2.20984 8.94999 2.26984 9.21999 2.45984 9.36999L4.56984 11.02C4.52984 11.34 4.49984 11.67 4.49984 12C4.49984 12.33 4.52984 12.66 4.56984 12.98L2.45984 14.63C2.26984 14.78 2.21984 15.05 2.33984 15.27L4.33984 18.73C4.39879 18.8332 4.49256 18.912 4.60434 18.9523C4.71612 18.9926 4.8386 18.9918 4.94984 18.95L7.43984 17.95C7.95984 18.35 8.51984 18.68 9.12984 18.93L9.50984 21.58C9.53984 21.82 9.74984 22 9.99984 22H13.9998C14.2498 22 14.4598 21.82 14.4898 21.58L14.8698 18.93C15.4798 18.68 16.0398 18.34 16.5598 17.95L19.0498 18.95C19.1098 18.97 19.1698 18.98 19.2298 18.98C19.3998 18.98 19.5698 18.89 19.6598 18.73L21.6598 15.27C21.7798 15.05 21.7298 14.78 21.5398 14.63L19.4298 12.98ZM17.4498 11.27C17.4898 11.58 17.4998 11.79 17.4998 12C17.4998 12.21 17.4798 12.43 17.4498 12.73L17.3098 13.86L18.1998 14.56L19.2798 15.4L18.5798 16.61L17.3098 16.1L16.2698 15.68L15.3698 16.36C14.9398 16.68 14.5298 16.92 14.1198 17.09L13.0598 17.52L12.8998 18.65L12.6998 20H11.2998L11.1098 18.65L10.9498 17.52L9.88984 17.09C9.45984 16.91 9.05984 16.68 8.65984 16.38L7.74984 15.68L6.68984 16.11L5.41984 16.62L4.71984 15.41L5.79984 14.57L6.68984 13.87L6.54984 12.74C6.51984 12.43 6.49984 12.2 6.49984 12C6.49984 11.8 6.51984 11.57 6.54984 11.27L6.68984 10.14L5.79984 9.43999L4.71984 8.59999L5.41984 7.38999L6.68984 7.89999L7.72984 8.31999L8.62984 7.63999C9.05984 7.31999 9.46984 7.07999 9.87984 6.90999L10.9398 6.47999L11.0998 5.34999L11.2998 3.99999H12.6898L12.8798 5.34999L13.0398 6.47999L14.0998 6.90999C14.5298 7.08999 14.9298 7.31999 15.3298 7.61999L16.2398 8.31999L17.2998 7.88999L18.5698 7.37999L19.2698 8.58999L18.1998 9.43999L17.3098 10.14L17.4498 11.27ZM11.9998 7.99999C9.78984 7.99999 7.99984 9.78999 7.99984 12C7.99984 14.21 9.78984 16 11.9998 16C14.2098 16 15.9998 14.21 15.9998 12C15.9998 9.78999 14.2098 7.99999 11.9998 7.99999ZM11.9998 14C10.8998 14 9.99984 13.1 9.99984 12C9.99984 10.9 10.8998 9.99999 11.9998 9.99999C13.0998 9.99999 13.9998 10.9 13.9998 12C13.9998 13.1 13.0998 14 11.9998 14Z"
                  fill="#FFFFFF8F"
                />
              </svg>
              Settings
            </span>
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default Leftbar;
